/*
 * The record that an operator forced a draft for one conversation (`just ops draft-one`).
 *
 * Its own append-only table rather than a new kind in `conversation_events`, because a deploy can
 * roll back automatically and a rollback runs the previous build without running the migration
 * down. That build parses the shared event vocabulary through a closed discriminated union, so a
 * kind it does not know took out the whole conversation's activity read, and with it the
 * approve-a-reply path. A table the previous build has never heard of is invisible to it, which is
 * what a rollback needs. Migration 046 carries the measurement.
 *
 * Read additively: the activity view asks for these separately and merges them, so nothing about
 * the shared event stream changes.
 */
import type { Pool, PoolClient } from "pg";
import { z } from "zod";
import type { WorkspaceScope } from "./workspace-scope.ts";

export const ForcedDraftRecordSchema = z.strictObject({
  planId: z.string().min(1),
  providerEventId: z.number().int().positive(),
  providerReplyId: z.number().int().positive(),
  lifted: z.enum(["drafting_off", "back_drafting_disabled", "backfill_not_drafted"]),
  reason: z.string().min(1).max(400),
  actorId: z.string().min(1).max(200),
  requestedAt: z.iso.datetime({ offset: true }),
});
export type ForcedDraftRecord = z.infer<typeof ForcedDraftRecordSchema>;
export const RedraftLiftRecordSchema = ForcedDraftRecordSchema.extend({
  /**
   * An operator's automatic-reply lift, a rep's "Draft anyway" on an outside-goal stop, a rep's
   * "Send our link" on a lead-link stop (`lead-scheduling-link.ts`), or a rep's Yes on a
   * conditional no (`conditional-no.ts`).
   */
  lifted: z.enum(["auto_reply_stop", "outside_goal_stop", "lead_link_stop", "conditional_no_stop"]),
  attempt: z.number().int().positive(),
});
export type RedraftLiftRecord = z.infer<typeof RedraftLiftRecordSchema>;

/**
 * Appends one record in the caller's transaction, so a forced draft without its record, or a record
 * without its forced draft, cannot commit. Re-forcing the same event adds nothing: the row is keyed
 * by the event it belongs to, and the first reason stands.
 */
export async function recordForcedDraft(
  client: PoolClient,
  scope: WorkspaceScope,
  input: Omit<ForcedDraftRecord, "requestedAt">,
): Promise<void> {
  const value = ForcedDraftRecordSchema.omit({ requestedAt: true }).parse(input);
  await client.query(
    `INSERT INTO draft_force_records
       (organization_id,workspace_id,plan_id,provider_event_id,provider_reply_id,lifted,reason,actor_id)
     SELECT $1,$2,$3,$4,$5,$6,$7,$8
     FROM work_plans plans
     WHERE plans.organization_id=$1 AND plans.workspace_id=$2 AND plans.id=$3
     ON CONFLICT (organization_id,plan_id,provider_event_id) DO NOTHING`,
    [
      scope.organizationId,
      scope.workspaceId,
      value.planId,
      value.providerEventId,
      value.providerReplyId,
      value.lifted,
      value.reason,
      value.actorId,
    ],
  );
}

/** One conversation's forced drafts, oldest first, scoped to the workspace that owns the plan. */
export async function forcedDraftsFor(
  pool: Pool | PoolClient,
  scope: WorkspaceScope,
  planId: string,
): Promise<ForcedDraftRecord[]> {
  const result = await pool.query(
    `SELECT plan_id AS "planId",provider_event_id AS "providerEventId",
            provider_reply_id AS "providerReplyId",lifted,reason,actor_id AS "actorId",
            to_char(requested_at AT TIME ZONE 'UTC','YYYY-MM-DD"T"HH24:MI:SS.MS"Z"') AS "requestedAt"
     FROM draft_force_records
     WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3
       AND EXISTS (SELECT 1 FROM work_plans
         WHERE organization_id=$1 AND workspace_id=$2 AND id=$3)
     ORDER BY requested_at,provider_event_id`,
    [scope.organizationId, scope.workspaceId, planId],
  );
  return result.rows.map((row) =>
    ForcedDraftRecordSchema.parse({
      ...row,
      providerEventId: Number(row.providerEventId),
    }),
  );
}

/** Saved-run auto-stop lifts travel separately so older browser bundles still parse forcedDrafts. */
export async function redraftLiftsFor(
  pool: Pool | PoolClient,
  scope: WorkspaceScope,
  planId: string,
): Promise<RedraftLiftRecord[]> {
  const result = await pool.query(
    `SELECT plan_id AS "planId",provider_event_id AS "providerEventId",
              provider_reply_id AS "providerReplyId",lifted_hold AS lifted,attempt,reason,
              actor_id AS "actorId",
              to_char(requested_at AT TIME ZONE 'UTC','YYYY-MM-DD"T"HH24:MI:SS.MS"Z"') AS "requestedAt"
       FROM draft_redraft_requests
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3
         AND lifted_hold IN ('auto_reply_stop','outside_goal_stop','lead_link_stop',
           'conditional_no_stop')
         AND EXISTS (SELECT 1 FROM work_plans
           WHERE organization_id=$1 AND workspace_id=$2 AND id=$3)
       ORDER BY requested_at,provider_event_id,attempt`,
    [scope.organizationId, scope.workspaceId, planId],
  );
  return result.rows.map((row) =>
    RedraftLiftRecordSchema.parse({
      ...row,
      providerEventId: Number(row.providerEventId),
    }),
  );
}

/**
 * The reason a rep's "Draft again" records (`draft-again.ts`). It is how Activity knows the rows
 * that control wrote: the table has no column for where a request came from, and adding one is a
 * migration.
 */
export const DRAFT_AGAIN_REASON = "Draft again, chosen in the conversation.";

/**
 * A rep's "Draft again": a redraft that lifted nothing, asked from the open conversation. It travels
 * in a field of its own, beside `redraftLifts` and never inside it: that field's `lifted` is a
 * closed list in every browser bundle already loaded, and a rollback serves the previous one.
 */
export const PlainRedraftRecordSchema = ForcedDraftRecordSchema.omit({ lifted: true }).extend({
  attempt: z.number().int().positive(),
});
export type PlainRedraftRecord = z.infer<typeof PlainRedraftRecordSchema>;

/**
 * One conversation's "Draft again" requests, oldest first, scoped as the lifts are. Only the rows
 * that control wrote: the same table holds an operator's redrafts, whose reason is free text typed
 * for a ticket, and the redrafts a saved material or an expired question started. None of those is
 * a rep asking for this draft again, and an operator's words are not a rep's to read.
 */
async function plainRedraftsFor(
  pool: Pool | PoolClient,
  scope: WorkspaceScope,
  planId: string,
): Promise<PlainRedraftRecord[]> {
  const result = await pool.query(
    `SELECT plan_id AS "planId",provider_event_id AS "providerEventId",
              provider_reply_id AS "providerReplyId",attempt,reason,actor_id AS "actorId",
              to_char(requested_at AT TIME ZONE 'UTC','YYYY-MM-DD"T"HH24:MI:SS.MS"Z"') AS "requestedAt"
       FROM draft_redraft_requests
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND lifted_hold IS NULL
         AND reason=$4
         AND EXISTS (SELECT 1 FROM work_plans
           WHERE organization_id=$1 AND workspace_id=$2 AND id=$3)
       ORDER BY requested_at,provider_event_id,attempt`,
    [scope.organizationId, scope.workspaceId, planId, DRAFT_AGAIN_REASON],
  );
  return result.rows.map((row) =>
    PlainRedraftRecordSchema.parse({
      ...row,
      providerEventId: Number(row.providerEventId),
    }),
  );
}

/**
 * Everything the activity route sends beside the events, each in its own field. The three are read
 * here so the route names them once and a new kind of record is added in this file, not in the app.
 * Plain redrafts are read only where asked: they are shown where "Draft again" is switched on, so a
 * deploy changes no workspace's Activity by itself.
 */
export async function draftRecordsFor(
  pool: Pool,
  scope: WorkspaceScope,
  planId: string,
  show: { plainRedrafts: boolean },
): Promise<{
  forcedDrafts: ForcedDraftRecord[];
  redraftLifts: RedraftLiftRecord[];
  plainRedrafts: PlainRedraftRecord[];
}> {
  const [forcedDrafts, redraftLifts, plainRedrafts] = await Promise.all([
    forcedDraftsFor(pool, scope, planId),
    redraftLiftsFor(pool, scope, planId),
    show.plainRedrafts ? plainRedraftsFor(pool, scope, planId) : [],
  ]);
  return { forcedDrafts, redraftLifts, plainRedrafts };
}

/**
 * When a new attempt was last asked for against the run the conversation still shows, or null. The
 * request names the run it replaces, so it stops matching the moment the new attempt's result is
 * shown: the wait is read from the record, never from a watcher.
 */
export async function redraftRequestedSince(
  pool: Pool | PoolClient,
  scope: WorkspaceScope,
  planId: string,
  shownRunId: string,
): Promise<string | null> {
  const result = await pool.query(
    `SELECT to_char(max(requested_at) AT TIME ZONE 'UTC','YYYY-MM-DD"T"HH24:MI:SS.MS"Z"') AS since
       FROM draft_redraft_requests
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND prior_run_id=$4`,
    [scope.organizationId, scope.workspaceId, planId, shownRunId],
  );
  return z.object({ since: z.string().nullable() }).parse(result.rows[0]).since;
}
